import { compactSmall } from "../../../helpers/formatters"
import { cx } from "@emotion/css"
import { fetchTableData } from "../../../api"

import { Button } from "@material-ui/core"
import { RowType } from "../../../types"
import { tableStyles } from "../../../styles"
import { useI18n } from "providers/I18n"
import { useQuery } from "react-query"
import { useRevenueParams } from "../../../hooks/useRevenueParams"
import { useMemo, useState } from "react"
import { useSortAndOrder } from "../../../hooks/useSortAndOrder"
import { RootLayout } from "./RootLayout"
import { TableTitle } from "./TableTitle"
import { ContentLayout } from "../../SharedComponents/ContentLayout"
import { FooterLayout } from "../../SharedComponents/FooterLayout"
import { FooterTable } from "../../SharedComponents/FooterTable"
import { mapViewGridFooter } from "../../../styles"
import { brandsRow, marketsRow } from "../../../tablesInfoData/tableRows"
import { BaseTable } from "../../SharedComponents/BaseTable"

const TableTab = ({
  isActive,
  onClick,
  text,
}: {
  isActive: boolean
  onClick: () => void
  text: string
}) => {
  return (
    <Button
      classes={{
        label: cx(
          isActive ? tableStyles.tabSelected : tableStyles.tabNotSelected,
        ),
      }}
      onClick={onClick}
    >
      <span className="HeadingH5Regular">{text}</span>
    </Button>
  )
}

export function BaseMapDoubleTable() {
  const { t } = useI18n()
  const { search, brandParam, regionParam, isStream } = useRevenueParams()
  const [isDualTableViewBrand, setIsDualTableViewBrand] = useState(true)
  const [errorBrands, setErrorBrands] = useState<boolean>(false)
  const [errorMarkets, setErrorMarkets] = useState<boolean>(false)
  const dualTableColumns = isDualTableViewBrand
    ? brandsRow(t) || []
    : marketsRow(t) || []

  const url = useMemo(() => {
    if (isStream) {
      return "client/summary/"
    } else if (brandParam === "GCF") {
      return "client/GCF/"
    }
    return `region/${regionParam}/`
  }, [brandParam, isStream, regionParam])

  const { data: markets, isLoading: isLoadingMarkets } = useQuery(
    ["markets", url + "markets", search],
    fetchTableData,
    {
      enabled: !!search,
      onSuccess: () => (!!errorMarkets ? setErrorMarkets(false) : null),
      onError: () => setErrorMarkets(true),
    },
  )
  const { data: brands, isLoading: isLoadingBrands } = useQuery(
    ["brands", url + "brands", search],
    fetchTableData,
    {
      enabled: !!search,
      onSuccess: () => (!!errorBrands ? setErrorBrands(false) : null),
      onError: () => setErrorBrands(true),
    },
  )

  const footerData = isDualTableViewBrand ? brands : markets

  const { sortAndOrder, onSort } = useSortAndOrder<RowType>(
    isDualTableViewBrand ? brands?.brands : markets?.markets,
  )
  const footer = [
    { value: t("revenue.totalrevenue"), align: "left" },
    {
      value: `${
        footerData?.totalRevenue
          ? "$" + compactSmall(footerData?.totalRevenue)
          : "N/A"
      }`,
      align: "center",
    },
    {
      value: `${
        footerData?.totalGrowth
          ? compactSmall(footerData?.totalGrowth) + "%"
          : "N/A"
      }`,
      align: "center",
    },
    {
      value: `${
        footerData?.totalMargin
          ? compactSmall(footerData?.totalMargin) + "%"
          : "N/A"
      }`,
      align: "center",
    },
  ]

  return (
    <RootLayout>
      <TableTitle
        title={
          <div className={tableStyles.border}>
            <TableTab
              isActive={isDualTableViewBrand}
              onClick={() => setIsDualTableViewBrand(true)}
              text={t("revenue.revenuestreamsbreakdown")}
            />
            <TableTab
              isActive={!isDualTableViewBrand}
              onClick={() => setIsDualTableViewBrand(false)}
              text={t("revenue.marketsummary")}
            />
          </div>
        }
      />
      <ContentLayout maxHeight="416px">
        <BaseTable
          hasError={errorBrands || errorMarkets}
          rows={dualTableColumns || []}
          data={isDualTableViewBrand ? brands?.brands : markets?.markets || []}
          isLoading={isLoadingBrands || isLoadingMarkets}
          onSort={onSort}
          selectedSortField={sortAndOrder.sort}
          selectedSortOrder={sortAndOrder.order}
          columnWidth={540 - 48}
          gap={0}
          cellsPerColumn={4}
        />
      </ContentLayout>
      <FooterLayout customClass={mapViewGridFooter.root}>
        <FooterTable
          footer={footer}
          columnWidth={540 - 48}
          gap={0}
          columnsLength={4}
        />
      </FooterLayout>
    </RootLayout>
  )
}
